import {
  Conversation,
  StartConversationParams,
  StartConversationResponse,
} from '@stripe-internal/conversations-client';
import {ApiMethods, ErrorResponse} from './ApiMethods';
import {
  ConversationsApiCreateConversationEndpointRequest,
  ConversationsApiDataConversation,
} from '../generated/types';
import {FrameworkAdapter} from './FrameworkAdapter';

const PARAMS_NOT_TO_FORWARD_TO_SUPPORT_CONVERSATIONS = ['forestVersion'];
const DELEGATE_FIRST_MESSAGE_TO_SERVER_MAX_ATTEMPTS = 3;

type ValidatedStartConversationParams = StartConversationParams & {
  participant: NonNullable<StartConversationParams['participant']>;
  context: NonNullable<StartConversationParams['context']> & {
    audience: ConversationsApiCreateConversationEndpointRequest['audience'];
    entryPoint: ConversationsApiCreateConversationEndpointRequest['entry_point'];
  };
};

type CreateConversationResult = Awaited<
  ReturnType<ApiMethods['createConversation']>
>;

type SendMessageResult =
  | {type: 'success'; data: ConversationsApiDataConversation}
  | ErrorResponse;

type GetConversationResult = Awaited<ReturnType<ApiMethods['getConversation']>>;

type FrameworkConversation = ReturnType<
  typeof FrameworkAdapter.fromApiConversation
>;

export type StartConversationCreateProcessFailureMetricTags = {
  failure_step: 'delegate_first_message_to_server';
  reason: 'exception' | 'server_error';
  attempt_count: number;
  error_code?: number;
};

type DelegateFirstMessageToServerFailureMetricTags = Omit<
  StartConversationCreateProcessFailureMetricTags,
  'failure_step'
>;

type RetrieveStartConversationParams = StartConversationParams & {
  conversationId: string;
  participant: NonNullable<StartConversationParams['participant']>;
};

type StartConversationHandlerDeps = {
  createConversation: (
    payload: ConversationsApiCreateConversationEndpointRequest,
  ) => ReturnType<ApiMethods['createConversation']>;
  sendMessage: (
    conversationId: string,
    payload: Parameters<ApiMethods['sendMessage']>[1],
  ) => Promise<SendMessageResult>;
  getConversation: (
    conversationId: string,
  ) => ReturnType<ApiMethods['getConversation']>;
  buildConversationInstance: ({
    frameworkConversation,
    params,
  }: {
    frameworkConversation: FrameworkConversation;
    params: ValidatedStartConversationParams | RetrieveStartConversationParams;
  }) => Conversation;
  emitDelegateFirstMessageToServerFailureMetric?: (
    tags: StartConversationCreateProcessFailureMetricTags,
  ) => void;
};

export class StartConversationHandler {
  private deps: StartConversationHandlerDeps;

  constructor(deps: StartConversationHandlerDeps) {
    this.deps = deps;
  }

  call = async (
    params: StartConversationParams,
  ): Promise<StartConversationResponse> => {
    if (params.conversationId) {
      if (!params.participant) {
        return StartConversationHandler.error(400, 'Missing user');
      }
      const retrieveParams: RetrieveStartConversationParams = {
        ...params,
        conversationId: params.conversationId,
        participant: params.participant,
      };
      const response = await this.deps.getConversation(params.conversationId);
      return this.toRetrieveConversationResponse(response, retrieveParams);
    }

    const validatedParams =
      StartConversationHandler.validateStartConversationParams(params);
    if (!validatedParams) {
      return StartConversationHandler.requiredParamError(params);
    }

    const startPayload =
      StartConversationHandler.buildStartPayload(validatedParams);
    const response = await this.deps.createConversation(startPayload);

    if (response.type === 'error') {
      return this.toStartConversationResponse(response, validatedParams);
    }

    const sendMessageResponse =
      await this.delegateFirstMessageToServerWithRetries(
        response.data.id,
        validatedParams,
        startPayload.page_context,
      );

    return this.toStartConversationResponse(
      sendMessageResponse,
      validatedParams,
    );
  };

  private async delegateFirstMessageToServerWithRetries(
    conversationId: string,
    params: ValidatedStartConversationParams,
    pageContext: ConversationsApiCreateConversationEndpointRequest['page_context'],
    attempt = 1,
  ): Promise<SendMessageResult> {
    try {
      /* On create, we send an empty message to the server to tell server to go first */
      const response = await this.deps.sendMessage(conversationId, {
        id: ApiMethods.generateMessageId(),
        participant_id: params.participant.id,
        participant_type: FrameworkAdapter.participantTypeFromRole(
          params.participant.role,
        ),
        parts: [],
        page_context: pageContext,
      });

      if (
        response.type !== 'error' ||
        !StartConversationHandler.shouldRetryApiError(response)
      ) {
        return response;
      }

      if (attempt === DELEGATE_FIRST_MESSAGE_TO_SERVER_MAX_ATTEMPTS) {
        this.emitDelegateFirstMessageToServerFailureMetric({
          reason: 'server_error',
          attempt_count: attempt,
          error_code: response.code,
        });
        return response;
      }
    } catch (e) {
      if (attempt === DELEGATE_FIRST_MESSAGE_TO_SERVER_MAX_ATTEMPTS) {
        this.emitDelegateFirstMessageToServerFailureMetric({
          reason: 'exception',
          attempt_count: attempt,
        });
        throw e;
      }
    }

    return this.delegateFirstMessageToServerWithRetries(
      conversationId,
      params,
      pageContext,
      attempt + 1,
    );
  }

  private emitDelegateFirstMessageToServerFailureMetric(
    tags: DelegateFirstMessageToServerFailureMetricTags,
  ): void {
    this.deps.emitDelegateFirstMessageToServerFailureMetric?.({
      failure_step: 'delegate_first_message_to_server',
      ...tags,
    });
  }

  private static shouldRetryApiError(response: SendMessageResult): boolean {
    return response.type === 'error' && response.code >= 500;
  }

  private static validateStartConversationParams(
    params: StartConversationParams,
  ): ValidatedStartConversationParams | null {
    if (!params.participant) {
      return null;
    }

    if (!params.context?.audience) {
      return null;
    }

    if (!params.context?.entryPoint) {
      return null;
    }

    return params as ValidatedStartConversationParams;
  }

  private static requiredParamError(
    params: StartConversationParams,
  ): StartConversationResponse {
    if (!params.participant) {
      return StartConversationHandler.error(400, 'Missing user');
    }

    if (!params.context?.audience) {
      return StartConversationHandler.error(400, 'Missing audience');
    }

    return StartConversationHandler.error(400, 'Missing entrypoint');
  }

  private static getLocale(
    context: ValidatedStartConversationParams['context'],
  ): string {
    if (typeof context.locale === 'string' && context.locale.length > 0) {
      return context.locale;
    }
    const documentLang = StartConversationHandler.getDocumentLang();
    if (typeof documentLang === 'string' && documentLang.length > 0) {
      return documentLang;
    }
    const navigatorLanguage = StartConversationHandler.getNavigatorLanguage();
    if (typeof navigatorLanguage === 'string' && navigatorLanguage.length > 0) {
      return navigatorLanguage;
    }
    return 'en';
  }

  private static buildStartPayload(
    params: ValidatedStartConversationParams,
  ): ConversationsApiCreateConversationEndpointRequest {
    const {context} = params;
    const urlSearchParams = StartConversationHandler.getUrlSearchParams();
    const forestVersion = urlSearchParams.get('forestVersion') || undefined;
    const pageContext = StartConversationHandler.buildPageContext(
      urlSearchParams,
      context,
      params.data,
    );

    return {
      audience: context.audience,
      entry_point: context.entryPoint,
      trigger_point: (context.triggerParams?.triggerPoint ||
        'unspecified') as ConversationsApiCreateConversationEndpointRequest['trigger_point'],
      locale: StartConversationHandler.getLocale(context),
      forest_version: forestVersion,
      refcode: context.triggerParams?.refcode,
      page_context:
        Object.keys(pageContext).length > 0 ? pageContext : undefined,
    };
  }

  private static buildPageContext(
    searchParams: URLSearchParams,
    context: ValidatedStartConversationParams['context'],
    data: StartConversationParams['data'],
  ): Record<string, string> {
    const pageContext = StartConversationHandler.buildDataPageContext(data);

    Array.from(searchParams.entries())
      .filter(
        ([key]) =>
          !PARAMS_NOT_TO_FORWARD_TO_SUPPORT_CONVERSATIONS.includes(key),
      )
      .forEach(([key, value]) => {
        pageContext[key] = value;
      });

    if (context.triggerParams?.refcode && !pageContext.refcode) {
      pageContext.refcode = context.triggerParams.refcode;
    }

    if (
      context.assistantAccessibleViaSearch &&
      !pageContext.assistant_accessible_via_search
    ) {
      pageContext.assistant_accessible_via_search = String(
        context.assistantAccessibleViaSearch,
      );
    }

    return pageContext;
  }

  private static buildDataPageContext(
    data: StartConversationParams['data'],
  ): Record<string, string> {
    const pageContext: Record<string, string> = {};
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return pageContext;
    }

    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }
      if (typeof value === 'string') {
        pageContext[key] = value;
        return;
      }
      if (
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'bigint'
      ) {
        pageContext[key] = String(value);
        return;
      }
      if (value === null) {
        pageContext[key] = 'null';
        return;
      }
      try {
        pageContext[key] = JSON.stringify(value);
      } catch {
        pageContext[key] = String(value);
      }
    });

    return pageContext;
  }

  private toStartConversationResponse(
    response: CreateConversationResult,
    params: ValidatedStartConversationParams,
  ): StartConversationResponse {
    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    const frameworkConversation = FrameworkAdapter.fromApiConversation({
      apiConversation: response.data,
      requestContext: params.context,
      user: params.participant,
    });

    const conversation = this.deps.buildConversationInstance({
      frameworkConversation,
      params,
    });

    return {code: 200, data: {conversation}};
  }

  private toRetrieveConversationResponse(
    response: GetConversationResult,
    params: RetrieveStartConversationParams,
  ): StartConversationResponse {
    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    const frameworkConversation = FrameworkAdapter.fromApiConversation({
      apiConversation: response.data,
      requestContext: params.context ?? {},
      user: params.participant,
    });

    const conversation = this.deps.buildConversationInstance({
      frameworkConversation,
      params,
    });

    return {code: 200, data: {conversation}};
  }

  private static error(code: number, error: string): StartConversationResponse {
    return {
      code,
      error,
    };
  }

  private static getUrlSearchParams() {
    if (typeof window === 'undefined') {
      return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
  }

  private static getDocumentLang(): string | undefined {
    if (typeof document === 'undefined') {
      return undefined;
    }
    return document.documentElement.lang;
  }

  private static getNavigatorLanguage(): string | undefined {
    if (typeof navigator === 'undefined') {
      return undefined;
    }
    return navigator.language;
  }
}
